import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  TextInput,
  Platform,
  useWindowDimensions,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { AuthContext } from "../../Context/AuthContext";
import { useFocusEffect } from "@react-navigation/native";
import GlobalStyles from "../../components/StylenColor";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";

// Desktop web imports
import {
  useDesktopDetect,
  DashboardLayout,
  DesktopTheme,
  getScoreColor,
  getPillarColor,
} from "../../components/web";

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

// Desktop Contributor Card Component
const DesktopContributorCard = ({ employee, onPress }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate average score from topics
  const averageScore = employee.topics.length > 0
    ? employee.topics.reduce((sum, t) => sum + t.score, 0) / employee.topics.length
    : 0;

  const scoreColor = getScoreColor(averageScore);

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <TouchableOpacity
      style={[
        desktopStyles.contributorCard,
        isHovered && desktopStyles.contributorCardHovered,
      ]}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      activeOpacity={0.8}
    >
      {/* Avatar */}
      <View style={[desktopStyles.avatar, { backgroundColor: scoreColor + '20' }]}>
        <Text style={[desktopStyles.avatarText, { color: scoreColor }]}>
          {getInitials(employee.userName)}
        </Text>
      </View>

      {/* Employee Info */}
      <View style={desktopStyles.employeeInfo}>
        <Text style={desktopStyles.employeeName} numberOfLines={1}>
          {employee.userName}
        </Text>
        <Text style={desktopStyles.employeeId}>
          ID: {employee.employeeIdNum}
        </Text>
      </View>

      {/* Department Badge */}
      <View style={desktopStyles.departmentBadge}>
        <Text style={desktopStyles.departmentText} numberOfLines={1}>
          {employee.department}
        </Text>
      </View>

      {/* Score */}
      <View style={desktopStyles.scoreContainer}>
        <Text style={[desktopStyles.scoreValue, { color: scoreColor }]}>
          {Math.round(averageScore)}
        </Text>
        <Text style={desktopStyles.scoreLabel}>avg score</Text>
      </View>

      {/* Topics Completed */}
      <View style={desktopStyles.topicsCount}>
        <Text style={desktopStyles.topicsNumber}>{employee.topics.length}</Text>
        <Text style={desktopStyles.topicsLabel}>
          {employee.topics.length === 1 ? 'topic' : 'topics'}
        </Text>
      </View>

      {/* Topic Tags */}
      {employee.topics.length > 0 && (
        <View style={desktopStyles.topicTags}>
          {employee.topics.slice(0, 3).map((topic, index) => (
            <View key={topic.topic} style={desktopStyles.topicTag}>
              <Text style={desktopStyles.topicTagText} numberOfLines={1}>
                {topic.topic}
              </Text>
              <View style={[desktopStyles.topicScoreDot, { backgroundColor: getScoreColor(topic.score) }]} />
            </View>
          ))}
          {employee.topics.length > 3 && (
            <Text style={desktopStyles.moreTopics}>
              +{employee.topics.length - 3} more
            </Text>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
};

// Desktop Contributors View
const DesktopContributorsView = ({
  employees,
  filteredEmployees,
  searchQuery,
  setSearchQuery,
  isRefreshing,
  onRefresh
}) => {
  // Calculate stats
  const totalTopicsSubmitted = employees.reduce((sum, emp) => sum + emp.topics.length, 0);
  const uniqueDepartments = [...new Set(employees.map(emp => emp.department))].length;
  const avgScore = employees.length > 0
    ? employees.reduce((sum, emp) => {
        const empAvg = emp.topics.length > 0
          ? emp.topics.reduce((s, t) => s + t.score, 0) / emp.topics.length
          : 0;
        return sum + empAvg;
      }, 0) / employees.length
    : 0;

  return (
    <DashboardLayout
      currentRoute="Contributors"
      pageTitle="Contributors"
      pageDescription="Employees who have completed questionnaires"
    >
      <ScrollView
        style={desktopStyles.container}
        contentContainerStyle={desktopStyles.contentContainer}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
        }
      >
        {/* Stats Row */}
        <View style={desktopStyles.statsRow}>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{employees.length}</Text>
            <Text style={desktopStyles.statLabel}>Total Contributors</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{totalTopicsSubmitted}</Text>
            <Text style={desktopStyles.statLabel}>Topics Submitted</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{uniqueDepartments}</Text>
            <Text style={desktopStyles.statLabel}>Departments</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={[desktopStyles.statValue, { color: getScoreColor(avgScore) }]}>
              {Math.round(avgScore)}%
            </Text>
            <Text style={desktopStyles.statLabel}>Average Score</Text>
          </View>
        </View>

        {/* Search Bar */}
        <View style={desktopStyles.searchSection}>
          <View style={desktopStyles.searchContainer}>
            <Text style={desktopStyles.searchIcon}>🔍</Text>
            <TextInput
              style={desktopStyles.searchInput}
              placeholder="Search by name, department, or employee ID..."
              placeholderTextColor={colors.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Text style={desktopStyles.clearIcon}>✕</Text>
              </TouchableOpacity>
            )}
          </View>
          <Text style={desktopStyles.resultCount}>
            {filteredEmployees.length} {filteredEmployees.length === 1 ? 'contributor' : 'contributors'}
            {searchQuery && ` matching "${searchQuery}"`}
          </Text>
        </View>

        {/* Contributors Grid */}
        {filteredEmployees.length > 0 ? (
          <View style={desktopStyles.contributorsGrid}>
            {filteredEmployees.map((employee) => (
              <DesktopContributorCard
                key={employee.employeeIdNum}
                employee={employee}
              />
            ))}
          </View>
        ) : (
          <View style={desktopStyles.emptyContainer}>
            <Text style={desktopStyles.emptyIcon}>👥</Text>
            <Text style={desktopStyles.emptyTitle}>No Contributors Found</Text>
            <Text style={desktopStyles.emptyText}>
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'No employees have filled the questionnaire yet'}
            </Text>
          </View>
        )}
      </ScrollView>
    </DashboardLayout>
  );
};

const Contributors = () => {
  const [employees, setEmployees] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { companyId } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState("");
  const { width } = useWindowDimensions();
  const isWeb = Platform.OS === "web";
  const isMobileWeb = isWeb && width <= 768;

  // Desktop detection
  const { isDesktopWeb } = useDesktopDetect();

  // Function to fetch latest employee responses
  const fetchEmployeeData = async () => {
    if (!companyId) {
      console.error("No companyId found in AuthContext");
      return;
    }

    try {
      setIsRefreshing(true);
      const q = query(
        collection(db, `companies/${companyId}/questionnaireResponses`),
        where("companyId", "==", companyId)
      );

      const snapshot = await getDocs(q);
      const employeeResponses = {};

      snapshot.forEach((doc) => {
        const {
          userName,
          employeeIdNum,
          department,
          topic,
          responseId,
          totalScore,
          timestamp,
        } = doc.data();

        if (
          userName &&
          employeeIdNum &&
          department &&
          topic &&
          responseId &&
          totalScore !== undefined
        ) {
          const responseDate = timestamp?.toDate?.() || new Date(0);

          // Initialize employee if not present
          if (!employeeResponses[employeeIdNum]) {
            employeeResponses[employeeIdNum] = {
              userName,
              employeeIdNum,
              department,
              topics: {},
            };
          }

          // For each topic, keep only the latest
          const existingTopic = employeeResponses[employeeIdNum].topics[topic];
          if (!existingTopic || existingTopic.timestamp < responseDate) {
            employeeResponses[employeeIdNum].topics[topic] = {
              topic,
              score: totalScore,
              responseId,
              timestamp: responseDate,
            };
          }
        }
      });

      // Transform topics object to array
      const employeeList = Object.values(employeeResponses).map((employee) => ({
        ...employee,
        topics: Object.values(employee.topics).sort(
          (a, b) => b.timestamp - a.timestamp
        ),
      }));

      setEmployees(employeeList);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    const query = searchQuery.toLowerCase();
    return (
      employee.userName.toLowerCase().includes(query) ||
      employee.department.toLowerCase().includes(query) ||
      employee.employeeIdNum.toLowerCase().includes(query)
    );
  });

  // Fetch data on mount
  useEffect(() => {
    fetchEmployeeData();
  }, [companyId]);

  useFocusEffect(
    useCallback(() => {
      fetchEmployeeData();
    }, [companyId])
  );

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <DesktopContributorsView
        employees={employees}
        filteredEmployees={filteredEmployees}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isRefreshing={isRefreshing}
        onRefresh={fetchEmployeeData}
      />
    );
  }

  // Mobile version (unchanged)
  return (
    <View style={styles.container}>
      <Text style={[styles.header, GlobalStyles.btntxtoffset]}>
        Employees Who Filled the Questionnaire
      </Text>
      <TextInput
        style={styles.searchInput}
        placeholder="Search by name, department, or ID..."
        placeholderTextColor="#999"
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
      <FlatList
        data={filteredEmployees}
        keyExtractor={(item) => item.employeeIdNum}
        key={isWeb && !isMobileWeb ? "web-2col" : "mobile-1col"} // Force re-render when layout changes
        numColumns={isWeb && !isMobileWeb ? 2 : 1}
        renderItem={({ item }) => (
          <View style={[styles.card, isWeb && !isMobileWeb && styles.cardWeb]}>
            <Text style={styles.userName}>{item.userName}</Text>
            <Text style={styles.details}>
              Employee ID: <Text style={styles.bold}>{item.employeeIdNum}</Text>{" "}
              | Department: <Text style={styles.bold}>{item.department}</Text>
            </Text>
            {Array.isArray(item.topics) &&
              item.topics.map((topicObj) => (
                <Text key={topicObj.topic} style={styles.details}>
                  ➤ Topic: <Text style={styles.bold}>{topicObj.topic}</Text> |
                  Score: <Text style={styles.bold}>{topicObj.score}</Text>
                </Text>
              ))}
          </View>
        )}
        columnWrapperStyle={
          isWeb && !isMobileWeb
            ? { justifyContent: "space-between", gap: 20 }
            : null
        }
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={fetchEmployeeData}
          />
        }
        contentContainerStyle={{ paddingBottom: 20 }}
      />
    </View>
  );
};

// Desktop styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.contentBg,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },

  // Stats Row
  statsRow: {
    flexDirection: 'row',
    marginBottom: spacing.lg,
    gap: spacing.md,
  },
  statCard: {
    flex: 1,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    alignItems: 'center',
    ...shadows.sm,
  },
  statValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h2,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  statLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Search Section
  searchSection: {
    marginBottom: spacing.lg,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...shadows.sm,
    marginBottom: spacing.sm,
  },
  searchIcon: {
    fontSize: 18,
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    outline: 'none',
  },
  clearIcon: {
    fontSize: 16,
    color: colors.textMuted,
    marginLeft: spacing.sm,
    padding: spacing.xs,
  },
  resultCount: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    marginLeft: spacing.sm,
  },

  // Contributors Grid
  contributorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },

  // Contributor Card
  contributorCard: {
    width: 'calc(33.333% - 11px)',
    minWidth: 300,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...shadows.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  contributorCardHovered: {
    ...shadows.md,
    borderColor: colors.primary,
    transform: [{ translateY: -2 }],
  },

  // Avatar
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  avatarText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h4,
  },

  // Employee Info
  employeeInfo: {
    marginBottom: spacing.sm,
  },
  employeeName: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h4,
    color: colors.textPrimary,
    marginBottom: 2,
  },
  employeeId: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Department Badge
  departmentBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primaryLight,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
    marginBottom: spacing.md,
  },
  departmentText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.tiny,
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Score
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: spacing.sm,
  },
  scoreValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h2,
    marginRight: spacing.xs,
  },
  scoreLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Topics Count
  topicsCount: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: spacing.md,
  },
  topicsNumber: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    marginRight: spacing.xs,
  },
  topicsLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Topic Tags
  topicTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  topicTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.contentBg,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  topicTagText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.tiny,
    color: colors.textSecondary,
    maxWidth: 100,
  },
  topicScoreDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginLeft: spacing.xs,
  },
  moreTopics: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.tiny,
    color: colors.textMuted,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },

  // Empty State
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxl * 2,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    ...shadows.sm,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h3,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
    textAlign: 'center',
  },
});

// **Mobile Styles** (unchanged)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
    padding: 20,
  },
  header: {
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    color: "white",
    backgroundColor: GlobalStyles.GlobalBg.headerColor,
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  card: {
    backgroundColor: "#effdee",
    padding: 15,
    marginVertical: 5,
    borderRadius: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  userName: {
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    color: "#007c7c",
    paddingVertical: 1,
  },
  details: {
    fontSize: GlobalStyles.fontSize.xss,
    color: "gray",
    marginTop: 5,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    paddingVertical: 1,
  },
  bold: {
    color: "#333",
  },
  searchInput: {
    backgroundColor: "#fff",
    padding: 10,
    borderRadius: 8,
    marginBottom: 10,
    borderColor: "#ccc",
    borderWidth: 1,
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    color: "#333",
  },
  cardWeb: {
    flex: 1,
    marginHorizontal: 5,
    minWidth: "48%",
  },
});

export default Contributors;
